import { serverOptionsFilter } from "@dehub/theme";
import { Box, Button, Popover, Select, Skeleton, Table, Text } from "@mantine/core";
import { MonthPicker } from "@mantine/dates";
import { useDebouncedValue } from "@mantine/hooks";
import { DownloadSimpleIcon, FileTextIcon, PlusIcon, XIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSwr from "swr";
import { NetworkApi, ReconciliationApi } from "../../apis";
import { Page, PageHeader } from "../../components/layout/Page";
import { ListFooter, ListToolbar, PartyCell, RowActions, type ToolbarTab, ListStates, resolveListStates } from "../../components/list";
import sharedListClasses from "../../components/list/list.module.css";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import { usePermission } from "../../components/permission";
import { IAuth } from "../../interfaces/IAuth";
import { currencyFormat } from "../../utils";
import { dateFormat } from "../../utils/date";
import { formatPeriod } from "../../utils/reconciliation-period";
import { renderBusinessName } from "../../utils/render-business-name";
import { ubFormat } from "../../utils/ub-time";
import classes from "./List.module.css";
import { ReasonTooltip } from "../../components/ui/reason-tooltip";
import { StatusBadge } from "../../components/ui/status-badge";

const COLUMN_COUNT = 10;
const SKELETON_ROWS = 6;

/** Төлөвийн цэгийн өнгө (StatusBadge): батлагдсан ногоон, татгалзсан улаан, засварласан шар, бусад саарал. */
const STATUS_DOT: Record<string, string> = {
  APPROVED: "green",
  REJECTED: "red",
  REVISED: "yellow",
};

/** Табын утга → backend-ийн `status` (Илгээсэн = SENT + VIEWED). */
const TAB_STATUS: Record<string, string> = {
  DRAFT: "DRAFT",
  SENT: "SENT,VIEWED",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
};

type ListQuery = {
  tab: string;
  month: string | null;
  partner: string | null;
  q: string;
  page: number;
  limit: number;
};

function readQuery(params: URLSearchParams): ListQuery {
  const tab = params.get("tab") ?? "";
  return {
    tab: tab in TAB_STATUS ? tab : "",
    month: /^\d{4}-\d{2}$/.test(params.get("month") ?? "") ? params.get("month") : null,
    partner: params.get("partner") || null,
    q: params.get("q") || "",
    page: Math.max(1, parseInt(params.get("page") || "1", 10) || 1),
    limit: Math.max(1, parseInt(params.get("limit") || "40", 10) || 40),
  };
}

function writeQuery(q: ListQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.tab) p.set("tab", q.tab);
  if (q.month) p.set("month", q.month);
  if (q.partner) p.set("partner", q.partner);
  if (q.q) p.set("q", q.q);
  if (q.page !== 1) p.set("page", String(q.page));
  if (q.limit !== 40) p.set("limit", String(q.limit));
  return p;
}

/** Цуцалсан (−) ба өмнөх сарын нэхэмжлэхийн залруулга (±): хуулгын хөдөлгөөний цэвэр дүн. */
const adjustmentOf = (r: any) => Number(r?.totals?.totalAmendments || 0) - Number(r?.totals?.totalCancelled || 0);

const amount = (v: unknown) => currencyFormat(Number(v || 0)).replace(/\s*₮$/, "");

export const StatementList = () => {
  const { t } = useTranslation(["reconciliation", "common"]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { myBusinessType, user } = useSelector((state: { auth: IAuth }) => state.auth);
  const myBusinessId = user?.currentBusiness?.id;
  const isBuyer = myBusinessType === "BUYER";
  const sfx = isBuyer ? "Buyer" : "";

  const q = useMemo(() => readQuery(searchParams), [searchParams]);

  const patchQuery = useCallback(
    (patch: Partial<ListQuery>, resetPage = true) => {
      setSearchParams(writeQuery({ ...q, ...patch, ...(resetPage ? { page: 1 } : {}) }), { replace: true });
    },
    [q, setSearchParams],
  );

  // Хайлт URL-ээс тусдаа state: бичих бүрт хүсэлт явуулахгүй.
  const [searchInput, setSearchInput] = useState(q.q);
  const [searchDebounced] = useDebouncedValue(searchInput, 600);
  useEffect(() => {
    if (searchDebounced.trim() !== q.q) patchQuery({ q: searchDebounced.trim() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounced]);
  useEffect(() => {
    if (q.q !== searchDebounced.trim()) setSearchInput(q.q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.q]);

  const [monthOpen, setMonthOpen] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [partnerLabel, setPartnerLabel] = useState<string | null>(null);
  const { query: partnerSearch, setQuery: setPartnerSearch, data: partnerSelects } = useDebouncedSearch<any>({
    key: "reconciliation.partner",
    fetcher: (query) => NetworkApi.select({ query }),
    enabled: partnerOpen || !!q.partner,
  });
  useEffect(() => {
    if (!q.partner) {
      setPartnerLabel(null);
      return;
    }
    const found = (partnerSelects ?? []).find((b: any) => b?.id === q.partner);
    if (found) setPartnerLabel(renderBusinessName(found, "-"));
  }, [q.partner, partnerSelects]);

  const params = useMemo(() => {
    const month = q.month ? dayjs(`${q.month}-01`) : null;
    return {
      page: q.page,
      limit: q.limit,
      ...(q.tab ? { status: TAB_STATUS[q.tab] } : {}),
      ...(q.partner ? { counterpartyBusinessId: q.partner } : {}),
      // Хуучин мөрүүд UB-ийн сарын 1-ийг UTC огноогоор (өмнөх өдөр) хадгалсан тул доод хязгаарыг 1 хоногоор сулруулна.
      ...(month ? { periodFrom: month.subtract(1, "day").format("YYYY-MM-DD"), periodTo: month.endOf("month").format("YYYY-MM-DD") } : {}),
      ...(q.q ? { search: q.q } : {}),
    };
  }, [q]);

  // backend-тэй ижил: жагсаалтад INV_SETTLEMENT_LIST эсвэл INV_RECONCILIATION_LIST аль нэг; «Шинэ» нь INV_SETT_SEND.
  const INV_SETTLEMENT_LIST = usePermission("INV_SETTLEMENT_LIST");
  const INV_RECONCILIATION_LIST = usePermission("INV_RECONCILIATION_LIST");
  const INV_SETT_SEND = usePermission("INV_SETT_SEND");
  const canList = INV_SETTLEMENT_LIST.hasAccess || INV_RECONCILIATION_LIST.hasAccess;

  const { data, error, isLoading, isValidating, mutate } = useSwr<any>(
    canList ? `reconciliation.list?${qs.stringify(params)}` : null,
    async () => await ReconciliationApi.list(params),
    { shouldRetryOnError: false, revalidateOnFocus: false },
  );

  const rows: any[] = useMemo(() => (data?.rows as any[]) ?? (Array.isArray(data) ? data : []), [data]);
  const total: number = data?.count ?? rows.length;
  const summary = data?.summary ?? null;
  const showError = !!error && !isValidating;
  const showSkeleton = isLoading || (isValidating && !data && !error);
  const kpiReady = !!summary && !showError;
  const dash = t("common:list.dash");
  const hasFilters = !!(q.tab || q.month || q.partner || q.q);
  const grandTotal: number = summary?.total ?? 0;
  const approvedPct = grandTotal > 0 ? Math.round(((summary?.approved ?? 0) / grandTotal) * 100) : 0;

  const goDetails = (id?: string) => {
    if (id) navigate(`/settlement/${id}`);
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const counterpartyName = (r: any) =>
    myBusinessId
      ? r?.sellerBusinessId === myBusinessId
        ? r?.buyerBusinessName
        : r?.sellerBusinessName
      : myBusinessType === "SUPPLIER"
        ? r?.buyerBusinessName
        : r?.sellerBusinessName;

  const statusLabel = (status?: string) => (status ? t(`reconciliation:status.${status}`, { defaultValue: status }) : "-");

  const tabs: ToolbarTab[] = [
    { value: "", label: t("reconciliation:tabs.all"), count: kpiReady ? summary.total : undefined },
    { value: "DRAFT", label: t("reconciliation:tabs.draft"), count: kpiReady ? summary.byStatus.DRAFT : undefined },
    { value: "SENT", label: t("reconciliation:tabs.sent"), count: kpiReady ? summary.awaiting : undefined },
    { value: "APPROVED", label: t("reconciliation:tabs.approved"), count: kpiReady ? summary.byStatus.APPROVED : undefined },
    { value: "REJECTED", label: t("reconciliation:tabs.rejected"), count: kpiReady ? summary.byStatus.REJECTED : undefined },
  ];

  const chipClass = (active: boolean) =>
    `${sharedListClasses.filterChip}${active ? ` ${sharedListClasses.filterChipActive}` : ""}`;

  const handleExport = () => {
    const header = [
      t("reconciliation:table.code"),
      t(`reconciliation:col.counterparty${sfx}`),
      t("reconciliation:col.period"),
      t("reconciliation:table.opening"),
      t("reconciliation:table.credit"),
      t("reconciliation:table.debit"),
      t("reconciliation:col.adjustment"),
      t("reconciliation:table.closing"),
      t("reconciliation:table.delta"),
      t("reconciliation:col.status"),
      t("reconciliation:col.date"),
    ];
    const lines = rows.map((r: any) => {
      const name = counterpartyName(r) ?? "-";
      const opening = Number(r?.openingBalance || 0);
      const credit = Number(r?.totalPayments || 0) + Number(r?.totalCreditNotes || 0);
      const debit =
        Number(r?.totalInvoiced || 0) +
        Number(r?.totalDebitNotes || 0) +
        Number(r?.totalLateFees || 0);
      const closing = Number(r?.closingBalance || 0);
      const adjustment = adjustmentOf(r);
      const delta = opening + debit - credit + adjustment - closing;
      return [
        r?.statementNumber ?? r?.refCode ?? "-",
        name,
        r?.periodFrom && r?.periodTo
          ? `${dateFormat(r.periodFrom)} ~ ${dateFormat(r.periodTo)}`
          : "-",
        opening,
        credit,
        debit,
        adjustment,
        closing,
        Math.abs(delta) < 0.01 ? t("reconciliation:table.match") : Math.abs(delta),
        statusLabel(r?.status),
        r?.createdAt ? dateFormat(r.createdAt) : "-",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",");
    });
    const csv = "﻿" + [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reconciliation-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderStatusCell = (r: any) => {
    const parts: string[] = [];
    if (r?.status === "APPROVED" && r?.approvedAt) {
      parts.push(t("reconciliation:sub.approved", { date: ubFormat(r.approvedAt, "MM-DD") }));
    } else if (r?.sentAt && ["SENT", "VIEWED", "REVISED"].includes(r?.status)) {
      parts.push(t("reconciliation:sub.sent", { date: ubFormat(r.sentAt, "MM-DD") }));
      if (Number(r?.remindersSent) > 0) {
        parts.push(t("reconciliation:sub.reminders", { count: Number(r.remindersSent) }));
      }
    }
    return (
      <Box className={classes.stack}>
        <StatusBadge color={STATUS_DOT[r?.status] ?? "gray"} label={statusLabel(r?.status)} />
        {parts.length > 0 && <Text span className={classes.sub}>{parts.join(" · ")}</Text>}
      </Box>
    );
  };

  const renderRow = (r: any, i: number) => {
    const closing = Number(r?.closingBalance || 0);
    const paid = Number(r?.totalPayments || 0) + Number(r?.totalCreditNotes || 0);
    const invoiced =
      Number(r?.totalInvoiced || 0) + Number(r?.totalDebitNotes || 0) + Number(r?.totalLateFees || 0);
    const adjustment = adjustmentOf(r);
    return (
      <Table.Tr key={r.id ?? i} className={sharedListClasses.scrollRow} onClick={() => goDetails(r.id)}>
        <Table.Td
          className={`${classes.colActions} ${sharedListClasses.stickyActions} ${sharedListClasses.stickyEdge}`}
          onClick={(e) => e.stopPropagation()}
        >
          <RowActions
            inlineOnly
            items={[
              {
                key: "view",
                intent: "view",
                icon: <FileTextIcon size={17} />,
                label: t("reconciliation:tooltip.details"),
                onClick: () => goDetails(r.id),
              },
              {
                key: "pdf",
                intent: "pdf",
                icon: <DownloadSimpleIcon size={17} />,
                label: t("reconciliation:tooltip.pdf"),
                onClick: () => window.open(r.pdfUrl, "_blank", "noopener"),
                hidden: !r?.pdfUrl,
              },
            ]}
          />
        </Table.Td>
        <Table.Td>
          <Text span className={classes.code}>{r?.statementNumber ?? r?.refCode ?? dash}</Text>
        </Table.Td>
        <Table.Td className={classes.colParty}>
          <PartyCell name={counterpartyName(r)} />
        </Table.Td>
        <Table.Td className={classes.period}>{formatPeriod(r?.periodFrom, r?.periodTo)}</Table.Td>
        <Table.Td className={`${classes.num} ${classes.numMuted}`}>{amount(r?.openingBalance)}</Table.Td>
        <Table.Td className={`${classes.num} ${classes.numMuted}`}>{invoiced ? amount(invoiced) : dash}</Table.Td>
        <Table.Td className={`${classes.num} ${classes.numMuted}`}>{paid ? amount(paid) : dash}</Table.Td>
        <Table.Td className={`${classes.num} ${classes.numMuted}`}>{adjustment ? amount(adjustment) : dash}</Table.Td>
        <Table.Td className={`${classes.num} ${classes.numStrong}`}>
          {closing < 0 ? (
            <Box className={classes.stackEnd}>
              {amount(closing)}
              <StatusBadge color="gray" label={t("reconciliation:advance")} />
            </Box>
          ) : (
            amount(closing)
          )}
        </Table.Td>
        <Table.Td className={classes.colStatus}>{renderStatusCell(r)}</Table.Td>
      </Table.Tr>
    );
  };

  const renderBody = () => {
    return rows.map(renderRow);
  };

  const listStates = resolveListStates(
    <ListStates
      loading={showSkeleton}
      error={showError ? error : undefined}
      onRetry={() => mutate()}
      empty={showError || showSkeleton || rows.length === 0}
      filtered={hasFilters}
      query={q.q}
      onClear={clearFilters}
      columns={COLUMN_COUNT}
    />
  );

  const th = (key: string, right?: boolean) => (
    // « / »-ийн өмнөх зайг тасрахгүй болгож «Цуцалсан / залруулга» 2 мөрөөс хэтрэхгүй.
    <Table.Th ta={right ? "right" : "left"}>{t(`reconciliation:col.${key === "counterparty" ? `counterparty${sfx}` : key}`).replace(" / ", " / ")}</Table.Th>
  );

  if (!canList) {
    return (
      <Page>
        <PageHeader title={t("reconciliation:title")} subtitle={t(`reconciliation:subtitle${sfx}`)} />
        {INV_SETTLEMENT_LIST.accessDenied()}
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        title={
          <>
            {t("reconciliation:title")}
            {kpiReady && <Text span className={classes.titleChip}>{summary.total}</Text>}
          </>
        }
        subtitle={t(`reconciliation:subtitle${sfx}`)}
        extra={
          <Box style={{ display: "flex", gap: 8 }}>
            <Button variant="default" leftSection={<DownloadSimpleIcon size={14} />} onClick={handleExport}>
              {t("reconciliation:actions.export")}
            </Button>
            {myBusinessType === "SUPPLIER" && (
              <ReasonTooltip reason={INV_SETT_SEND.reason}>
                <Button color="brand" leftSection={<PlusIcon size={14} weight="bold" />} onClick={() => navigate("/settlement/new")}>
                  {t("reconciliation:actions.new")}
                </Button>
              </ReasonTooltip>
            )}
          </Box>
        }
      />

      <Box className={classes.kpi}>
        <Box className={classes.kpiCell}>
          <Text span className={classes.kpiLabel}>{t("reconciliation:kpi.total")}</Text>
          <Text span className={classes.kpiValue}>{kpiReady ? summary.total : dash}</Text>
          <Text span className={classes.kpiHint}>
            {kpiReady ? t(`reconciliation:kpi.totalHint${sfx}`, { count: summary.counterparties }) : " "}
          </Text>
        </Box>
        <Box className={classes.kpiCell}>
          <Text span className={classes.kpiLabel}>{t(`reconciliation:kpi.approved${sfx}`)}</Text>
          <Text span className={classes.kpiValue}>
            {kpiReady ? summary.approved : dash}
            {kpiReady && <Text span className={classes.kpiDenom}>{` / ${summary.total}`}</Text>}
          </Text>
          <Box className={classes.kpiBar}>
            <Box className={classes.kpiBarFill} style={{ width: `${kpiReady ? approvedPct : 0}%` }} />
          </Box>
        </Box>
        <Box className={classes.kpiCell}>
          <Text span className={classes.kpiLabel}>{t(`reconciliation:kpi.awaiting${sfx}`)}</Text>
          <Text span className={classes.kpiValue}>{kpiReady ? summary.awaiting : dash}</Text>
          <Text span className={classes.kpiHint}>{t(`reconciliation:kpi.awaitingHint${sfx}`)}</Text>
        </Box>
        <Box className={classes.kpiCell}>
          <Text span className={classes.kpiLabel}>{t("reconciliation:kpi.rejected")}</Text>
          <Text
            span
            className={`${classes.kpiValue} ${classes.kpiValueSm} ${kpiReady ? (summary.rejected > 0 ? classes.kpiBad : classes.kpiOk) : ""}`}
          >
            {kpiReady ? summary.rejected : dash}
          </Text>
          <Text span className={`${classes.kpiHint} ${kpiReady ? (summary.rejected > 0 ? classes.kpiHintBad : classes.kpiHintOk) : ""}`}>
            {kpiReady && summary.rejected === 0 ? t("reconciliation:kpi.allMatch") : " "}
          </Text>
        </Box>
      </Box>

      <Box className={classes.card}>
        <ListToolbar
          tabs={tabs}
          activeTab={q.tab}
          onTabChange={(tab) => patchQuery({ tab })}
          search={{ value: searchInput, onChange: setSearchInput, placeholder: t(`reconciliation:search${sfx}`), w: 260 }}
          hasActiveFilters={hasFilters}
          clearFilters={clearFilters}
          filterSlot={
            <>
              <Popover opened={monthOpen} onChange={setMonthOpen} position="bottom-end" shadow="md" withinPortal>
                <Popover.Target>
                  <Box component="button" type="button" className={chipClass(!!q.month)} onClick={() => setMonthOpen((o) => !o)}>
                    {q.month ? (
                      <>
                        <Text span size="sm" fw={500}>{q.month}</Text>
                        <XIcon size={12} weight="bold" onClick={(e) => { e.stopPropagation(); patchQuery({ month: null }); setMonthOpen(false); }} />
                      </>
                    ) : (
                      <>
                        <PlusIcon size={12} weight="bold" />
                        <Text span size="sm">{t("reconciliation:chip.month")}</Text>
                      </>
                    )}
                  </Box>
                </Popover.Target>
                <Popover.Dropdown>
                  <MonthPicker
                    value={q.month ? `${q.month}-01` : null}
                    onChange={(v) => { patchQuery({ month: v ? dayjs(v).format("YYYY-MM") : null }); setMonthOpen(false); }}
                  />
                </Popover.Dropdown>
              </Popover>

              <Popover opened={partnerOpen} onChange={setPartnerOpen} position="bottom-end" shadow="md" withinPortal>
                <Popover.Target>
                  <Box component="button" type="button" className={chipClass(!!q.partner)} onClick={() => setPartnerOpen((o) => !o)}>
                    {q.partner ? (
                      <>
                        <Text span size="sm" fw={500}>{partnerLabel ?? t(`reconciliation:chip.partner${sfx}`)}</Text>
                        <XIcon size={12} weight="bold" onClick={(e) => { e.stopPropagation(); patchQuery({ partner: null }); setPartnerOpen(false); }} />
                      </>
                    ) : (
                      <>
                        <PlusIcon size={12} weight="bold" />
                        <Text span size="sm">{t(`reconciliation:chip.partner${sfx}`)}</Text>
                      </>
                    )}
                  </Box>
                </Popover.Target>
                <Popover.Dropdown>
                  <Select
                    searchable
                    clearable
                    comboboxProps={{ withinPortal: false }}
                    filter={serverOptionsFilter}
                    searchValue={partnerSearch}
                    onSearchChange={setPartnerSearch}
                    nothingFoundMessage={t("common:noResultsShort")}
                    data={(partnerSelects ?? []).map((item: any) => ({ label: renderBusinessName(item, ""), value: item?.id ?? "" }))}
                    value={q.partner}
                    onChange={(id) => {
                      const found = (partnerSelects ?? []).find((b: any) => b?.id === id);
                      setPartnerLabel(found ? renderBusinessName(found, "-") : null);
                      patchQuery({ partner: id });
                      setPartnerOpen(false);
                    }}
                    placeholder={t(`reconciliation:chip.partner${sfx}`)}
                    w={280}
                  />
                </Popover.Dropdown>
              </Popover>
            </>
          }
        />

        <Box className={classes.wrap}>
          {listStates ?? (
          <Table.ScrollContainer minWidth={1200}>
            <Table className={classes.table} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
              <Table.Thead>
                <Table.Tr className={sharedListClasses.scrollRow}>
                  <Table.Th className={`${classes.colActions} ${sharedListClasses.stickyActions} ${sharedListClasses.stickyEdge}`}>
                    <Text className={sharedListClasses.stickyActionsHead}>{t("reconciliation:col.actions")}</Text>
                  </Table.Th>
                  {th("code")}
                  {th("counterparty")}
                  {th("period")}
                  {th("opening", true)}
                  {th("invoiced", true)}
                  {th("paid", true)}
                  {th("adjustment", true)}
                  {th("closing", true)}
                  {th("status")}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{renderBody()}</Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          )}
        </Box>

        {!showError && (
          <ListFooter
            from={Math.min(total, (q.page - 1) * q.limit + 1)}
            to={Math.min(total, (q.page - 1) * q.limit + rows.length)}
            total={total}
            page={q.page}
            pageSize={q.limit}
            onPageChange={(p) => patchQuery({ page: p }, false)}
            onPageSizeChange={(limit) => patchQuery({ page: 1, limit })}
          />
        )}
      </Box>
    </Page>
  );
};
